import { FC } from 'react';
import { twMerge } from 'tailwind-merge';

import { AppFormattedMessage } from '../../../../../components/app-formatted-message/index';
import {
  VESTING_GRAPH_PLOT_RIGHT_INSET_PX,
  VESTING_GRAPH_Y_AXIS_WIDTH,
} from '../model/vesting-graph.constants';
import { formatVestingCalendarDate } from '../model/vesting-graph.format';
import { VestingGraphIntroPhase } from '../model/vesting-graph.intro';
import {
  DateFormatter,
  HoveredVestingPeriod,
  VestingGraphAxisTick,
  VestingGraphAxisTickLabel,
} from '../model/vesting-graph.types';
import { VestingGraphIntroReveal } from './vesting-graph-intro-reveal';

/**
 * Schedule endpoints are named under the axis. Cliff and today stay date-only — their
 * markers already carry the meaning. Accel / expiry dates live in the hover modal.
 */
const AXIS_TICK_HAS_CAPTION: Record<VestingGraphAxisTickLabel, boolean> = {
  CLIFF: false,
  FULLY_VESTED: true,
  GRANT_START: true,
  TODAY: false,
};

type VestingGraphAxisTicksProps = {
  axisTicks: VestingGraphAxisTick[];
  format: DateFormatter;
  hoveredPeriod: HoveredVestingPeriod | null;
  introDelayForReveal: (leftPercent: number) => number;
  introPhase: VestingGraphIntroPhase;
};

export const VestingGraphAxisTicks: FC<VestingGraphAxisTicksProps> = ({
  axisTicks,
  format,
  hoveredPeriod,
  introDelayForReveal,
  introPhase,
}) => (
  <div
    className="relative mt-1 h-10 text-[10px] font-medium text-gray-500"
    style={{
      marginLeft: VESTING_GRAPH_Y_AXIS_WIDTH,
      marginRight: VESTING_GRAPH_PLOT_RIGHT_INSET_PX,
    }}
  >
    {axisTicks.map((tick, index) => {
      // Picks out the date the hovered period starts on, when the axis shows it.
      const isHoveredPeriodStart =
        hoveredPeriod?.periodStartTimestamp === new Date(tick.date).getTime();
      const tickClassName = isHoveredPeriodStart ? 'text-brand-500' : 'text-gray-500';

      return (
        <div
          className={twMerge(
            'absolute flex flex-col items-center whitespace-nowrap text-center',
            tickClassName,
          )}
          key={`${tick.label ?? 'point'}-${tick.date}-${index}`}
          style={{
            left: `${tick.leftPercent}%`,
            transform: 'translateX(-50%)',
          }}
        >
          <VestingGraphIntroReveal
            className="flex flex-col items-center"
            delayMs={introDelayForReveal(tick.leftPercent)}
            phase={introPhase}
          >
            <span>{formatVestingCalendarDate(format, tick.date)}</span>
            {tick.label && AXIS_TICK_HAS_CAPTION[tick.label] && (
              <span>
                <AppFormattedMessage id={tick.label} />
              </span>
            )}
          </VestingGraphIntroReveal>
        </div>
      );
    })}
  </div>
);
